import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '@caro/shared/constants';
import type { AutoProvisionResult } from '@caro/shared/types';
import { useAuth } from './useAuth';

/**
 * Zero-touch device auto-provisioning (R1).
 *
 * Spec: specs/20260602-resident-device-autoprovision/plan.md §5 R1
 *
 * Device identity keys on the Hexnode UDID supplied in the managed-launch URL
 * (`https://app.caro.care?hexnodeUDID=<udid>`). On cold start when no
 * DeviceToken exists, the hook reads `hexnodeUDID` from `window.location.search`,
 * persists it to `sessionStorage` (so it survives SPA navigation), then strips
 * it from the URL via `history.replaceState` — mirroring the public-alarm-token
 * pattern in repo-root CLAUDE.md. When absent from the URL it falls back to the
 * persisted value. It POSTs `{ hexnodeUDID }` to `AUTH_AUTO_PROVISION`; on
 * success it stores the minted device JWT in the same auth store the qrActivate
 * / AdminSetupPage flow uses and routes to `/`.
 *
 * Fail-closed (no UDID present, or 404 / 409 / timeout) NEVER throws — it routes
 * to `/admin-setup` so the operator can complete the PIN fallback. AdminSetupPage
 * is unchanged.
 *
 * NOTE: this hook no longer depends on `NativeBridge.getDeviceInfo()` /
 * `@capacitor/device`; the UDID arrives via the managed-launch URL.
 */

export const HEXNODE_UDID_STORAGE_KEY = 'caro.hexnodeUDID';

export interface AutoProvisionDeps {
  /** True when a DeviceToken / access token is already stored. */
  hasToken: () => boolean;
  /** Raw URL query string, e.g. `window.location.search`. */
  getUrlSearch: () => string;
  /** Read the persisted UDID (sessionStorage). */
  getStored: (key: string) => string | null;
  /** Persist the UDID (sessionStorage). */
  setStored: (key: string, value: string) => void;
  /** Strip the `hexnodeUDID` param from the URL (history.replaceState). */
  stripUrl: () => void;
  /** POST helper (api.post). */
  post: <T>(url: string, body: unknown) => Promise<T>;
  /** Persist the minted device token (same store as qrActivate / admin flow). */
  storeToken: (token: string) => void;
  /**
   * Persist the user profile from the provision response. REQUIRED for the app
   * to become authenticated: the shared auth store only sets `isAuthenticated`
   * true via `setUser` (not `setAccessToken`), and a populated user also
   * suppresses the User-JWT `/me` hydration that a DeviceToken can't satisfy.
   */
  storeUser?: (user: unknown) => void;
  /** Router navigate. */
  navigate: (path: string) => void;
}

export interface AutoProvisionOutcome {
  provisioned: boolean;
  skipped?: boolean;
}

type AutoProvisionEnvelope = { success?: boolean; data?: AutoProvisionResult } & Partial<AutoProvisionResult>;

/**
 * Pure, dependency-injected core of the auto-provision flow. Exported so it can
 * be unit-tested without mounting React.
 */
export async function runAutoProvision(deps: AutoProvisionDeps): Promise<AutoProvisionOutcome> {
  // Whether the device is ALREADY authenticated. When true we run in "refresh"
  // mode: re-fetch the provisioning profile to pick up server-side changes
  // (UI language, building, extension) WITHOUT navigating, and — critically —
  // WITHOUT logging the device out if the refresh fails. When false this is a
  // cold start: success → '/', any failure → '/admin-setup' (manual PIN).
  const hadToken = deps.hasToken();

  // 1. Read the UDID from the URL first; persist + strip when present.
  let hexnodeUDID: string | null = null;
  try {
    const params = new URLSearchParams(deps.getUrlSearch() || '');
    const fromUrl = params.get('hexnodeUDID');
    if (fromUrl && fromUrl.trim()) {
      hexnodeUDID = fromUrl.trim();
      deps.setStored(HEXNODE_UDID_STORAGE_KEY, hexnodeUDID);
      deps.stripUrl();
    }
  } catch {
    // URL parsing failed — fall through to the persisted value.
  }

  // 2. Fall back to the persisted UDID (survives SPA navigation / refresh).
  if (!hexnodeUDID) {
    try {
      const cached = deps.getStored(HEXNODE_UDID_STORAGE_KEY);
      if (cached && cached.trim()) hexnodeUDID = cached.trim();
    } catch {
      // storage unavailable — treat as no UDID.
    }
  }

  // 3. No UDID anywhere.
  if (!hexnodeUDID) {
    // Cold start with no UDID → manual PIN flow. Already-authed (no UDID to
    // refresh with, e.g. an email/password session) → leave the session alone.
    if (!hadToken) deps.navigate('/admin-setup');
    return { provisioned: false };
  }

  try {
    const res = await deps.post<AutoProvisionEnvelope>(API_ENDPOINTS.AUTH_AUTO_PROVISION, {
      hexnodeUDID,
    });

    const result = (res?.data || res) as (AutoProvisionResult & { user?: unknown }) | undefined;
    const token = result?.deviceToken;

    if (!token) {
      if (!hadToken) deps.navigate('/admin-setup');
      return { provisioned: false };
    }

    // Store the (possibly refreshed) token + profile. setUser flips
    // `isAuthenticated` true and refreshes language/building/etc.
    deps.storeToken(token);
    if (result?.user && deps.storeUser) {
      deps.storeUser(result.user);
    }
    // Only navigate on a cold start; a refresh must not yank the user around.
    if (!hadToken) deps.navigate('/');
    return { provisioned: true };
  } catch {
    // Cold start failure → manual PIN fallback. Refresh failure (we already had
    // a valid session) → keep the existing session; do NOT log out / redirect.
    if (!hadToken) deps.navigate('/admin-setup');
    return { provisioned: false, skipped: hadToken };
  }
}

/**
 * React hook: runs auto-provision once on cold start when unauthenticated.
 * Wire it into the boot sequence before the `/login` redirect (App.tsx).
 */
// MODULE-LEVEL guard: auto-provision must run at most ONCE per page load, even
// if AutoProvisionBoot unmounts/remounts (e.g. a transient redirect to /login).
// A per-component ref resets on remount and caused a tight re-provision loop
// (~1 req/s) when the session briefly dropped. This survives remounts; a real
// cold start is a fresh page load, which resets it.
let autoProvisionAttempted = false;

export function useAutoProvision(): void {
  const { api, accessToken, setAccessToken, setUser } = useAuth();
  const navigate = useNavigate();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current || autoProvisionAttempted) return;
    // Runs once per page load (module guard survives remounts). On a cold start
    // (no token) it provisions; when already authenticated it REFRESHES the
    // profile (language/building/extension) if a hexnodeUDID is available —
    // runAutoProvision no-ops safely when there's a token but no UDID.
    ranRef.current = true;
    autoProvisionAttempted = true;

    void runAutoProvision({
      hasToken: () => Boolean(accessToken),
      getUrlSearch: () => (typeof window !== 'undefined' ? window.location.search : ''),
      getStored: (key) => {
        try {
          return window.sessionStorage.getItem(key);
        } catch {
          return null;
        }
      },
      setStored: (key, value) => {
        try {
          window.sessionStorage.setItem(key, value);
        } catch {
          // private mode / storage disabled — UDID stays in memory for this run.
        }
      },
      stripUrl: () => {
        try {
          const params = new URLSearchParams(window.location.search);
          params.delete('hexnodeUDID');
          const qs = params.toString();
          const cleanUrl = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
          window.history.replaceState({}, '', cleanUrl);
        } catch {
          // history API unavailable — leave the URL as-is.
        }
      },
      post: (url, body) => api.post(url, body),
      storeToken: (token) => {
        // The resident app uses the device JWT as its access token for
        // Authorization: Bearer (same store qrActivate / admin-setup write to).
        setAccessToken(token);
      },
      storeUser: (user) => {
        // setUser flips isAuthenticated → true and suppresses the /me hydration
        // that a DeviceToken can't satisfy. Without this the app bounces to /login.
        setUser(user as any);
      },
      navigate: (path) => navigate(path),
    });
  }, [api, accessToken, setAccessToken, setUser, navigate]);
}
